import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import {t} from 'i18next'
import axios from 'axios'
import Swal from 'sweetalert2'
import DataTable from 'react-data-table-component'
import { Badge, Button, Col, Input, Row } from 'reactstrap'

import { headersState } from '../../../recoil/recoil'
import { isNull, paginationComponentOptions } from '../../../izUtils'
import { axiosError } from '../../../helpers/response'
import Loader from '../../spinner/Loader'
import { entityName, fieldName, fieldValue, importTableStyles, WarningList } from './importHelpers'

const entityOrder = ['contract', 'facility_security_system', 'facility', 'facility_supervisor', 'security_system_component', 'client', 'contract_link', 'security_system', 'manufacturer', 'system', 'central_station'];

const recordTitle = ({entity, label}) => {
    switch (entity) {
        case 'contract': return label.contract_number;
        case 'client': return label.client;
        case 'facility': return label.facility;
        case 'facility_security_system': return label.facility + ' / ' + label.security_system;
        case 'security_system_component': return label.facility + ' / ' + label.security_system + ': ' + label.name;
        case 'facility_supervisor': return label.facility + ': ' + label.name;
        case 'contract_link': return label.contract_number + ' / ' + label.facility + ' / ' + label.security_system;
        default: return label.name;
    }
}

const RecordDetails = ({data, isOpen, onToggle}) => (
    <div className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
        <WarningList warnings={data.warnings} />
        {data.changes.length !== 0 &&
            <table className="table table-sm table-bordered mt-2 mb-0" style={{ backgroundColor: '#fff' }}>
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>{t('contracts.import.field')}</th>
                        <th>{t('contracts.import.oldValue')}</th>
                        <th>{t('contracts.import.newValue')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.changes.map(change => (
                        <tr key={change.key}>
                            <td>
                                {isOpen ? (
                                    <Input
                                        type="checkbox"
                                        className="contract-import-change"
                                        aria-label={change.action === 'create' ? t('contracts.import.newRecord') : fieldName(change.field)}
                                        checked={change.selected}
                                        onChange={() => onToggle(data, [change.key], !change.selected)}
                                    />
                                ) : (
                                    <span title={t('contracts.import.rowResult.' + change.result, {defaultValue: change.result})}>
                                        <i className={'fa ' + (['applied', 'required'].includes(change.result) ? 'fa-check text-success' : 'fa-minus text-muted')}></i>
                                    </span>
                                )}
                            </td>
                            {change.action === 'create' ? (
                                <td colSpan="3">
                                    <Badge color="success" className="me-2">{t('contracts.import.newRecord')}</Badge>
                                    {Object.keys(change.values).filter(field => !field.endsWith('_id')).map(field => fieldName(field) + ': ' + fieldValue(field, change.values[field])).join(', ')}
                                </td>
                            ) : (
                                <>
                                    <td>{fieldName(change.field)}</td>
                                    <td>{fieldValue(change.field, change.old)}</td>
                                    <td><b>{fieldValue(change.field, change.new)}</b></td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        }
    </div>
)

const ImportRecords = ({importData, apiUrl, rowsVersion, counts, onSelectionChange}) => {
    const Navigate = useNavigate();
    const headers = useRecoilValue(headersState);
    const isOpen = importData.status === 'analyzed';

    const [records, setRecords] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [entity, setEntity] = useState('contract')
    const [selectedFilter, setSelectedFilter] = useState('')
    const [search, setSearch] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(50)
    const [reload, setReload] = useState(0)

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSearchValue(search);
        }, 400);
        return () => clearTimeout(timer);
    }, [search])

    useEffect(() => {
        setLoading(true);

        let params = {entity, page, per_page: perPage};
        if (selectedFilter !== '') params.selected = selectedFilter;
        if (searchValue !== '') params.search = searchValue;

        axios.get(apiUrl + '/' + importData.id + '/records', {headers, params}).then(response => {
            setRecords(response.data.records.data);
            setTotal(response.data.records.total);
            onSelectionChange(response.data.counts, false);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            axiosError(error, Navigate);
        });
    }, [importData.id, entity, selectedFilter, searchValue, page, perPage, rowsVersion, reload]) // eslint-disable-line react-hooks/exhaustive-deps

    const changeFilter = (setter, value) => {
        setPage(1);
        setter(value);
    }

    // changeKeys null means every change of the record
    const toggle = (record, changeKeys, selected) => {
        axios.post(apiUrl + '/' + importData.id + '/select', {record_id: record.id, change_keys: changeKeys, selected}, {headers}).then(response => {
            setRecords(current => current.map(item => item.id === record.id ? response.data.record : item));
            onSelectionChange(response.data.counts, true);
        }).catch(error => axiosError(error, Navigate));
    }

    const selectGroup = (selected) => {
        const send = () => {
            axios.post(apiUrl + '/' + importData.id + '/select', {entity, selected}, {headers}).then(response => {
                setReload(value => value + 1);
                onSelectionChange(response.data.counts, true);
            }).catch(error => axiosError(error, Navigate));
        };

        if (!selected) {
            send();
            return;
        }

        // This also ticks the changes that were left unticked on purpose, because a person should look at them
        Swal.fire({
            text: t('contracts.import.selectAllWarning'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--theme-default)',
            confirmButtonText: t('contracts.import.selectAll'),
            cancelButtonText: t('cancel'),
        }).then(result => {
            if (result.isConfirmed) send();
        })
    }

    const columns = useMemo(() => {
        const list = [
            {
                name: t('contracts.import.record'),
                wrap: true,
                grow: 2,
                cell: record => <div><Badge color="light" className="text-dark me-2">{entityName(record.entity)}</Badge><b>{recordTitle(record)}</b></div>,
            },
            {
                name: t('table.client'),
                wrap: true,
                cell: record => record.label.clients.join(', '),
            },
            {
                name: t('contracts.import.table.rows'),
                width: '130px',
                wrap: true,
                cell: record => record.label.rows,
            },
            {
                name: t('contracts.import.table.changes'),
                width: '110px',
                cell: record => isOpen ? record.selected_changes + ' / ' + record.total_changes : record.total_changes,
            },
            {
                name: t('contracts.import.table.warnings'),
                width: '120px',
                cell: record => <span className={record.needs_review ? 'text-danger fw-bold' : ''}>{record.warnings.length}</span>,
            },
        ];

        if (isOpen && entity !== 'warnings') {
            list.unshift({
                name: '',
                width: '60px',
                cell: record => (
                    <Input
                        type="checkbox"
                        className="contract-import-record"
                        aria-label={recordTitle(record)}
                        checked={record.selected_changes === record.total_changes}
                        innerRef={element => { if (!isNull(element)) element.indeterminate = record.selected_changes > 0 && record.selected_changes < record.total_changes; }}
                        onChange={() => toggle(record, null, record.selected_changes !== record.total_changes)}
                    />
                ),
            });
        }

        return list;
    }, [isOpen, importData.id, entity]) // eslint-disable-line react-hooks/exhaustive-deps

    const entities = entityOrder.filter(item => !isNull(counts) && counts.entities[item]);
    const detailProps = useMemo(() => ({isOpen, onToggle: toggle}), [isOpen, importData.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <div className="mb-3" id="contract-import-entities">
                {entities.map(item => (
                    <Button key={'entity-' + item} color={entity === item ? 'primary' : 'light'} size="sm" className="me-2 mb-2" onClick={() => changeFilter(setEntity, item)}>
                        {entityName(item)} ({counts.entities[item]})
                    </Button>
                ))}
                {!isNull(counts) && counts.warnings !== 0 &&
                    <Button color={entity === 'warnings' ? 'primary' : 'light'} size="sm" className="me-2 mb-2" onClick={() => changeFilter(setEntity, 'warnings')}>
                        {t('contracts.import.onlyWarnings')} ({counts.warnings})
                    </Button>
                }
            </div>

            <Row className="mb-2 align-items-end">
                <Col md="4">
                    <label className="form-label" htmlFor="contract-import-search">{t('contracts.import.search')}</label>
                    <Input id="contract-import-search" type="text" value={search} onChange={event => setSearch(event.target.value)} />
                </Col>
                {isOpen && entity !== 'warnings' &&
                    <>
                        <Col md="3">
                            <label className="form-label" htmlFor="contract-import-selected-filter">{t('contracts.import.selectedFilter.title')}</label>
                            <Input id="contract-import-selected-filter" type="select" value={selectedFilter} onChange={event => changeFilter(setSelectedFilter, event.target.value)}>
                                <option value="">{t('contracts.import.selectedFilter.all')}</option>
                                <option value="true">{t('contracts.import.selectedFilter.selected')}</option>
                                <option value="false">{t('contracts.import.selectedFilter.notSelected')}</option>
                            </Input>
                        </Col>
                        <Col md="5" className="text-end">
                            <Button color="secondary" size="sm" className="me-2" onClick={() => selectGroup(true)}>{t('contracts.import.selectAll')}</Button>
                            <Button color="secondary" size="sm" onClick={() => selectGroup(false)}>{t('contracts.import.unselectAll')}</Button>
                        </Col>
                    </>
                }
            </Row>

            <p className="small text-muted">{t('contracts.import.recordsNote')}</p>

            <div style={{ position: 'relative' }}>
                {loading && <Loader />}
                <DataTable
                    key={entity + selectedFilter + importData.id}
                    columns={columns}
                    data={records}
                    keyField="id"
                    customStyles={importTableStyles}
                    expandableRows
                    expandableRowsComponent={RecordDetails}
                    expandableRowsComponentProps={detailProps}
                    pagination
                    paginationServer
                    paginationTotalRows={total}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[25, 50, 100, 200]}
                    paginationComponentOptions={paginationComponentOptions(t)}
                    onChangePage={value => setPage(value)}
                    onChangeRowsPerPage={(value, currentPage) => { setPerPage(value); setPage(currentPage); }}
                    noDataComponent={t('contracts.import.noRecords')}
                />
            </div>
        </div>
    )
}

export default ImportRecords
